<template>
  <div class="flex flex-col h-full overflow-hidden">
    <!-- Progress Indicator -->
    <report-progress :showProgress="isLoading" />

    <!-- Table Container with Sticky Header -->
    <div class="flex-1 overflow-hidden">
      <div class="h-full flex flex-col">
        <!-- Single Table with Sticky Header -->
        <div class="flex-1 overflow-auto">
          <table class="w-full table-auto text-sm">
            <thead class="sticky top-0 z-10">
              <tr>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium rounded-s-lg mx-1 px-2 py-2"
                >
                  {{ $t("reports.calls_report.head_id") }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                >
                  {{ $t("reports.calls_report.head_number") }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                >
                  {{ $t("reports.calls_report.head_agent") }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                >
                  {{ $t("reports.calls_report.head_call_type") }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                >
                  {{ $t("reports.calls_report.head_queue") }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                >
                  {{ $t("reports.calls_report.head_duration") }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                >
                  {{ $t("reports.calls_report.head_status") }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                >
                  {{ $t("reports.calls_report.head_hungparty") }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                >
                  {{ $t("reports.calls_report.head_reason") }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                >
                  {{ $t("reports.calls_report.head_sa") }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2"
                >
                  {{ $t("reports.calls_report.head_wait") }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium border-r-2 border-white px-2 py-2"
                >
                  {{ $t("reports.calls_report.head_date") }}
                </th>
                <th
                  class="!bg-primary-900 text-white set-border text-center font-weight-medium rounded-e-lg px-2 py-2"
                >
                  <!-- Actions column -->
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(call, index) in reportData"
                :key="call.uniqueid"
                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
              >
                <td class="text-center !py-2 px-2 border-b border-gray-200">
                  {{ call.uniqueid }}
                </td>
                <td
                  class="text-center text-indigo px-2 border-b border-gray-200"
                >
                  {{ call.call_type == 2 ? call.dst : call.src }}
                </td>
                <td
                  class="text-center text-grey-darken-1 px-2 border-b border-gray-200"
                >
                  {{ getAgentFullName(call.agent) }}
                </td>
                <td
                  :class="
                    'text-center text-uppercase text-' +
                    callType(call.call_type).color +
                    ' px-2 border-b border-gray-200'
                  "
                >
                  <v-tooltip
                    :text="callType(call.call_type).type"
                    location="top"
                  >
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="small">{{
                        callType(call.call_type).icon
                      }}</v-icon>
                    </template>
                  </v-tooltip>
                </td>
                <td class="text-center px-2 border-b border-gray-200">
                  {{
                    skills.find((skill) => skill.name === call.shortcode)
                      ? $i18n.locale === "ar"
                        ? skills.find((skill) => skill.name === call.shortcode)
                            .titles.ar
                        : skills.find((skill) => skill.name === call.shortcode)
                            .titles.en
                      : call.shortcode
                  }}
                </td>
                <td
                  class="text-center text-primary-900 px-2 border-b border-gray-200"
                >
                  {{ call.duration }}
                </td>
                <td
                  :class="
                    'text-center text-uppercase text-' +
                    callStatus(call).color +
                    ' px-2 border-b border-gray-200'
                  "
                >
                  <v-tooltip :text="callStatus(call).status" location="top">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props" size="small">{{
                        callStatus(call).icon
                      }}</v-icon>
                    </template>
                  </v-tooltip>
                </td>
                <td
                  class="text-center text-uppercase px-2 border-b border-gray-200"
                >
                  {{
                    callStatus(call).status ==
                      $t("reports.calls_report.ANSWERED") &&
                    call.hungparty != "-"
                      ? $t("reports.filters." + call.hungparty)
                      : "-"
                  }}
                </td>
                <td
                  class="text-center text-grey-darken-1 px-2 border-b border-gray-200"
                >
                  <v-tooltip
                    v-if="
                      call.reasons && call.reasons.length > 0 && call.reasons[0]
                    "
                    :text="call.reasons[0]"
                    location="top"
                  >
                    <template v-slot:activator="{ props }">
                      <span v-bind="props" class="cursor-pointer">
                        {{ getLastReasonPart(call.reasons[0]) }}
                      </span>
                    </template>
                  </v-tooltip>
                  <span v-else>-</span>
                </td>
                <td
                  class="text-center text-primary-900 px-2 border-b border-gray-200"
                >
                  {{ call.speed_ans?.split(".")[0] }}
                </td>
                <td
                  class="text-center text-primary-900 px-2 border-b border-gray-200"
                >
                  {{ call.wait_time?.split(".")[0] }}
                </td>
                <td class="text-center px-2 border-b border-gray-200">
                  {{ call.calldate }}
                </td>
                <td
                  v-if="
                    callStatus(call).status ==
                    $t('reports.calls_report.ANSWERED')
                  "
                  class="px-2 border-b border-gray-200"
                >
                  <div class="flex justify-center items-center gap-1">
                    <BubbleMenu
                      @opened="
                        currentAudioId == call.uniqueid
                          ? null
                          : getRecording(call, false)
                      "
                      position="top"
                      :closeOnMenuClick="false"
                    >
                      <template #trigger>
                        <div
                          class="bg-primary-900 rounded p-1 hover:bg-primary-500 text-white"
                        >
                          <v-progress-circular
                            v-if="loadingAudio[call.uniqueid]"
                            class="!h-4 !w-4"
                            width="2"
                            indeterminate
                            color="white"
                          />
                          <v-icon v-else size="small">mdi-headphones</v-icon>
                        </div>
                      </template>
                      <template #default>
                        <CustomAudioPlayer
                          :src="
                            loadingAudio[call.uniqueid] ? null : sourceAudio
                          "
                          :autoplay="false"
                          :loop="false"
                        />
                      </template>
                    </BubbleMenu>
                    <div
                      class="bg-primary-900 rounded hover:bg-primary-500 p-1 text-white cursor-pointer"
                      @click="getRecording(call, true)"
                    >
                      <v-progress-circular
                        v-if="downloadAudio[call.uniqueid]"
                        class="!h-4 !w-4"
                        width="2"
                        indeterminate
                        color="white"
                      />
                      <v-icon v-else size="small">mdi-download</v-icon>
                    </div>
                    <div
                      v-if="hasAiReportsPermission"
                      class="bg-primary-900 rounded hover:bg-primary-500 p-1 text-white cursor-pointer"
                      :title="$t('reports.call_analysis.action_tooltip')"
                      @click="openCallAnalysis(call)"
                    >
                      <v-icon size="small">mdi-chart-box-outline</v-icon>
                    </div>
                  </div>
                </td>
                <td v-else class="px-2 border-b border-gray-200"></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Transcription Dialog -->
    <Dialog
      v-model:visible="showTranscriptionDialog"
      :modal="true"
      :closable="true"
      :draggable="false"
      :dismissableMask="true"
      :closeOnEscape="true"
      :closeOnBackdrop="true"
      :breakpoints="{ '1199px': '75vw', '575px': '90vw' }"
      :style="{ width: '60vw' }"
      :header="`Call Transcription - ${transcriptionData?.callid || ''}`"
    >
      <div
        v-if="transcriptionLoading"
        class="flex justify-center items-center py-8"
      >
        <v-progress-circular
          indeterminate
          color="primary"
        ></v-progress-circular>
        <span class="ml-3">Loading transcription...</span>
      </div>

      <div v-else-if="transcriptionData" class="space-y-6">
        <!-- Diarized Transcript -->
        <div class="bg-gray-50 p-4 rounded-lg">
          <h3 class="font-semibold text-gray-700 mb-3">
            {{ $t("reports.transcription.diarized_transcript") }}
          </h3>
          <div class="space-y-3">
            <div
              v-for="(segment, index) in formattedDiarizedTranscript"
              :key="index"
              class="flex items-start gap-3 p-3 bg-white rounded-lg border-l-4"
              :class="
                segment.speaker === 'Speaker 1'
                  ? 'border-blue-500'
                  : 'border-green-500'
              "
            >
              <div class="flex-shrink-0">
                <span
                  class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                  :class="
                    segment.speaker === 'Speaker 1'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-green-100 text-green-800'
                  "
                >
                  {{ segment.speaker }}
                </span>
              </div>
              <p class="text-gray-900 flex-1">{{ segment.text }}</p>
            </div>
          </div>
        </div>

        <!-- Sentiment Analysis -->
        <div class="bg-gray-50 p-4 rounded-lg">
          <h3 class="font-semibold text-gray-700 mb-3">
            {{ $t("reports.transcription.sentiment_analysis") }}
          </h3>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div class="bg-white p-4 rounded-lg border-l-4 border-green-500">
              <div class="flex items-center justify-between mb-2">
                <span class="text-sm font-medium text-gray-700">{{
                  $t("reports.transcription.positive")
                }}</span>
                <span class="text-lg font-bold text-green-600"
                  >{{ (sentimentData.positive * 100).toFixed(1) }}%</span
                >
              </div>
              <div class="w-full bg-gray-200 rounded-full h-2">
                <div
                  class="bg-green-500 h-2 rounded-full transition-all duration-300"
                  :style="{ width: sentimentData.positive * 100 + '%' }"
                ></div>
              </div>
            </div>

            <div class="bg-white p-4 rounded-lg border-l-4 border-yellow-500">
              <div class="flex items-center justify-between mb-2">
                <span class="text-sm font-medium text-gray-700">{{
                  $t("reports.transcription.neutral")
                }}</span>
                <span class="text-lg font-bold text-yellow-600"
                  >{{ (sentimentData.neutral * 100).toFixed(1) }}%</span
                >
              </div>
              <div class="w-full bg-gray-200 rounded-full h-2">
                <div
                  class="bg-yellow-500 h-2 rounded-full transition-all duration-300"
                  :style="{ width: sentimentData.neutral * 100 + '%' }"
                ></div>
              </div>
            </div>

            <div class="bg-white p-4 rounded-lg border-l-4 border-red-500">
              <div class="flex items-center justify-between mb-2">
                <span class="text-sm font-medium text-gray-700">{{
                  $t("reports.transcription.negative")
                }}</span>
                <span class="text-lg font-bold text-red-600"
                  >{{ (sentimentData.negative * 100).toFixed(1) }}%</span
                >
              </div>
              <div class="w-full bg-gray-200 rounded-full h-2">
                <div
                  class="bg-red-500 h-2 rounded-full transition-all duration-300"
                  :style="{ width: sentimentData.negative * 100 + '%' }"
                ></div>
              </div>
            </div>
          </div>

          <!-- Overall Sentiment -->
          <div class="mt-4 p-3 bg-white rounded-lg">
            <div class="flex items-center justify-between">
              <span class="text-sm font-medium text-gray-700"
                >{{ $t("reports.transcription.overall_sentiment") }}:</span
              >
              <span
                class="px-3 py-1 rounded-full text-sm font-medium"
                :class="overallSentimentClass"
              >
                {{ overallSentimentText }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="transcriptionError" class="text-center py-8">
        <v-icon color="error" size="large">mdi-alert-circle</v-icon>
        <p class="text-red-600 mt-2">{{ transcriptionError }}</p>
      </div>
    </Dialog>
  </div>
</template>

<script>
import ReportProgress from "../../components/ReportProgress.vue";
import { mapActions, mapGetters } from "vuex";
import BubbleMenu from "@/components/BubbleMenu.vue";
import CustomAudioPlayer from "@/components/CustomAudioPlayer.vue";
import { Dialog } from "primevue";
// import TranscriptionService from "@/services/TranscriptionService.js";

export default {
  name: "call-report",
  components: {
    ReportProgress,
    BubbleMenu,
    CustomAudioPlayer,
    Dialog,
  },
  props: {
    selectedFilters: {
      type: Object,
      default: () => ({}),
    },
    skills: {
      type: Array,
      default: () => [],
    },
    agents: {
      type: Array,
      default: () => [],
    },
    reportData: {
      type: Array,
      default: () => [],
    },
    totalCount: {
      type: Number,
      default: 0,
    },
    showCount: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showAudio: false,
      audioSrc: "",
      openRecord: false,
      recordingInfo: null,
      sourceAudio: null,
      currentAudioId: null,
      loadingAudio: {},
      downloadAudio: {},
      showTranscriptionDialog: false,
      transcriptionData: null,
      transcriptionLoading: false,
      transcriptionError: null,
    };
  },
  computed: {
    currentLocal() {
      return this.$i18n.locale;
    },
    ...mapGetters(["domain", "uname", "token", "permissions", "aiReportsPermission"]),
    hasAiReportsPermission() {
      return this.userHasPermisson(this.permissions, this.aiReportsPermission);
    },
    sentimentData() {
      if (this.transcriptionData?.sentiment_analysis) {
        try {
          return JSON.parse(this.transcriptionData.sentiment_analysis);
        } catch (e) {
          return { positive: 0, neutral: 0, negative: 0 };
        }
      }
      return { positive: 0, neutral: 0, negative: 0 };
    },
    formattedDiarizedTranscript() {
      if (!this.transcriptionData?.diarized_transcript) return [];

      const segments = [];
      const text = this.transcriptionData.diarized_transcript;

      // Split by speaker patterns like [S1]: or [S2]:
      const speakerRegex = /\[S(\d+)\]:\s*/g;
      const parts = text.split(speakerRegex);

      for (let i = 1; i < parts.length; i += 2) {
        const speakerNum = parts[i];
        const text = parts[i + 1]?.trim();

        if (text) {
          segments.push({
            speaker: `Speaker ${speakerNum}`,
            text: text,
          });
        }
      }

      return segments;
    },
    overallSentimentText() {
      const { positive, neutral, negative } = this.sentimentData;

      if (positive > neutral && positive > negative) {
        return this.$t("reports.transcription.positive");
      } else if (negative > neutral && negative > positive) {
        return this.$t("reports.transcription.negative");
      } else {
        return this.$t("reports.transcription.neutral");
      }
    },
    overallSentimentClass() {
      const { positive, neutral, negative } = this.sentimentData;

      if (positive > neutral && positive > negative) {
        return "bg-green-100 text-green-800";
      } else if (negative > neutral && negative > positive) {
        return "bg-red-100 text-red-800";
      } else {
        return "bg-yellow-100 text-yellow-800";
      }
    },
  },
  methods: {
    ...mapActions(["GetRecordingFile", "SetMessageDialog"]),

    getAgentFullName(username) {
      if (!username) return "";

      const agent = this.agents.find((a) => a.value === username);
      return agent ? agent.label : username;
    },

    getLastReasonPart(fullReason) {
      if (!fullReason) return "-";
      // Split by ' - ' separator and get the last part (leaf reason)
      const parts = fullReason.split(" - ");
      return parts[parts.length - 1] || fullReason;
    },

    openCallAnalysis(call) {
      this.$router.push({
        name: "call-analysis",
        query: {
          call_id: call.uniqueid,
          number: call.call_type == 2 ? call.dst : call.src,
          agent: this.getAgentFullName(call.agent),
          date: call.calldate,
          recordingfile: call.recordingfile || "",
        },
      });
    },

    getRecording(call, download) {
      this.downloadAudio[call.uniqueid] = download;
      this.loadingAudio[call.uniqueid] = !download;

      const payload = {
        recordingfile: call.recordingfile,
        callid: call.uniqueid,
        time: call.calldate,
        download,
      };

      this.GetRecordingFile(payload)
        .then((resp) => {
          if (download) {
            // Download is already handled by the store action using fileDownload
            if (!resp.success) {
              this.SetMessageDialog({ show: true, message: resp.message });
            }
          } else {
            if (!resp.success) {
              this.SetMessageDialog({ show: true, message: resp.message });
            } else {
              const href = URL.createObjectURL(resp.recording);
              this.sourceAudio = href;
              this.currentAudioId = call.uniqueid;
            }
          }
        })
        .catch((err) => {
          let message = err.message;
          if (err.response) {
            message = err.response.data.message;
          }
          this.SetMessageDialog({ show: true, message: message });
        })
        .finally(() => {
          this.loadingAudio[call.uniqueid] = false;
          this.downloadAudio[call.uniqueid] = false;
        });
    },

    // async getTranscription(call) {
    //   this.transcriptionLoading = true;
    //   this.transcriptionError = null;
    //   this.transcriptionData = null;
    //   this.showTranscriptionDialog = true;

    //   try {
    //     const result = await TranscriptionService.getTranscription(
    //       call.uniqueid,
    //       call.calldate,
    //       this.uname,
    //       this.token,
    //       this.domain
    //     );

    //     if (result.success) {
    //       this.transcriptionData = result.data;
    //     } else {
    //       this.transcriptionError = result.message;
    //     }
    //   } catch (error) {
    //     this.transcriptionError = error.message || 'Failed to get transcription';
    //   } finally {
    //     this.transcriptionLoading = false;
    //   }
    // },

    callStatus(call) {
      if (
        call.dstchannel.length == 0 ||
        call.duration == "0" ||
        call.disposition.includes("NO ANSWER") ||
        call.disposition.includes("FAILED") ||
        call.disposition.includes("BUSY")
      ) {
        if (call.call_type == 1) {
          return {
            status: this.$t("reports.calls_report.DROP"),
            color: "error",
            icon: "mdi-phone-off",
          };
        } else if (call.call_type == 2) {
          return {
            status: this.$t("reports.calls_report.UNANSWERED"),
            color: "error",
            icon: "mdi-phone-remove",
          };
        }
      } else {
        return {
          status: this.$t("reports.calls_report.ANSWERED"),
          color: "success",
          icon: "mdi-phone-check",
        };
      }
    },

    callType(type) {
      if (type == 1) {
        return {
          type: this.$t("reports.calls_report.calltype_incoming"),
          icon: "mdi-phone-incoming",
          color: "green-darken-1",
        };
      } else if (type == 2) {
        return {
          type: this.$t("reports.calls_report.calltype_outgoing"),
          icon: "mdi-phone-outgoing",
          color: "blue-lighten-3",
        };
      } else if (type == 3) {
        return {
          type: this.$t("reports.calls_report.calltype_internal"),
          icon: "mdi-phone-in-talk",
          color: "cyan-lighten-3",
        };
      } else {
        return {
          type: this.$t("reports.calls_report.calltype_dial"),
          icon: "mdi-phone-classic",
        };
      }
    },

    getArabicPluralKey(key, count) {
      return count === 1 ? key : `${key}_plural`;
    },
  },
};
</script>

<style scoped></style>
